<template>
  <div
    v-intersect
    class="checkbox-filter"
    @intersect:enter="onShow"
  >
    <div class="checkbox-filter__input-wrapper">
      <transition name="filter-input">
        <BaseInput
          v-if="showMore"
          v-model.trim="searchValue"
          class="checkbox-filter__input"
          :placeholder="$t('catalog.search.placeholder')"
        />
      </transition>
    </div>

    <PerfectScrollbar ref="scrollBarRef" :options="options" class="checkbox-filter__scroll">
      <div class="checkbox-filter__values">
        <BaseCheckbox
          v-for="filterItem in items"
          :key="filterItem.value"
          class="checkbox-filter__item"
          :class="{ 'disabled-color': !filterItem.count }"
          :checked="hasChecked(filterItem)"
          @change="onChange(filterItem)"
        >
          <template v-if="isFilterWithLink">
            <a
              :href="getLinkFilter(filterItem)"
              class="checkbox-filter__link"
              rel="nofollow"
              @click.prevent="onChange(filterItem)"
            >{{ getLabel(filterItem) }}</a>
          </template>
          <template v-else>{{ getLabel(filterItem) }}</template>
        </BaseCheckbox>
      </div>
    </PerfectScrollbar>

    <a
      v-if="isMoreLimit"
      href="#"
      rel="nofollow"
      class="checkbox-filter__button link"
      @click.prevent="toggleShowMore"
      >{{
        showMore
          ? $t('catalog.filters.hide_more_filters')
          : $t('catalog.filters.show_more_filters', { count: numberOfSorted })
      }}</a
    >

    <div v-if="showMore && items.length === 0">
      {{ $t('catalog.filters.no_results') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { BRAND_GROUP_FILTER_CODE, CONDITION_FILTER_CODE } from '@/constants/common'
import type { CatalogFilter, CatalogFilterItem, FilterItemChangePayload, SelectedFilter } from '~/types/catalog'
import { options } from '~/constants/perfect-scrollbar'
type Props = {
  filter: CatalogFilter
  selected: string
  limit?: number
  showMore?: boolean
}

type Emits = {
  (e: 'show-more', value: boolean): void
  (e: 'on-change' | 'on-show', value: FilterItemChangePayload): void
}

const props = withDefaults(defineProps<Props>(), {
  limit: 5
})
const emit = defineEmits<Emits>()
const localePath = useLocalePath()
const i18n = useI18n()
const route = useRoute()

const scrollBarRef = useTemplateRef('scrollBarRef')

const searchValue = shallowRef('')
const filterWithLinks = inject<boolean>('filterWithLinks', false)
const selectedBrand = inject<ComputedRef<SelectedFilter | null>>('selectedBrand', computed(() => null))
const selectedCondition = inject<ComputedRef<SelectedFilter | null>>('selectedCondition', computed(() => null))

const searchIsUsed = computed(() => {
  return searchValue.value.length > 0
})

const selectedKeys = computed(() => {
  if (!props.selected.length) {
    return new Set()
  }
  return new Set(props.selected.split(','))
})

const isConditionFilter = computed(() => {
  return props.filter.code === CONDITION_FILTER_CODE
})

const isBrandFilter = computed(() => {
  return props.filter.code === BRAND_GROUP_FILTER_CODE
})

const filterItems = computed<CatalogFilterItem[]>(() => {
  const items: CatalogFilterItem[] = props.filter.items
    .filter((item: CatalogFilterItem) => {
      return item.slug !== 'drugie'
    }) || []

  if (!searchIsUsed.value) {
    return items
  }

  const regSearch: RegExp = new RegExp(searchValue.value, 'i')

  return items.filter((item: CatalogFilterItem) => {
    return regSearch.test(item.label)
  })
})

const sortedItems = computed<CatalogFilterItem[]>(() => {
  const filter: CatalogFilterItem[] = []
  const filterEmpty: CatalogFilterItem[] = []

  filterItems.value.forEach((it) => {
    if (it.count > 0) {
      filter.push(it)
    } else {
      filterEmpty.push(it)
    }
  })

  return filter.concat(filterEmpty)
})

const numberOfSorted = computed(() => {
  return sortedItems.value.length
})

const isMoreLimit = computed(() => {
  return numberOfSorted.value > props.limit
})

const items = computed<CatalogFilterItem[]>(() => {
  return isMoreLimit.value && !props.showMore
    ? sortedItems.value.slice(0, props.limit)
    : sortedItems.value
})

const isFilterWithLink = computed(() => {
  if (!filterWithLinks) {
    return filterWithLinks
  }
  const regularCondition = new RegExp(CONDITION_FILTER_CODE, 'i')
  const regularBrand = new RegExp(BRAND_GROUP_FILTER_CODE, 'i')
  const filterCode: string = props.filter.code

  return filterWithLinks &&
    (regularCondition.test(filterCode) || regularBrand.test(filterCode))
})

const hasChecked = ({ value }: CatalogFilterItem) => {
  return selectedKeys.value.has(value)
}

const getLabel = ({ label }: CatalogFilterItem) => {
  switch (label) {
    case 'True':
      return i18n.t('common.yes')
    case 'False':
      return i18n.t('common.no')
    default:
      return uppertFirst(label)
  }
}

const getLinkFilter = (filterItem: CatalogFilterItem) => {
  let categoryFilters: string = `${props.filter.code}=${filterItem.value}`

  const selectedConditionData = unref(selectedCondition)
  const selectedBrandData = unref(selectedBrand)

  if (isConditionFilter.value && selectedBrandData) {
    categoryFilters = `${selectedBrandData.code}=${selectedBrandData.value}&${categoryFilters}`
  } else if (isBrandFilter.value && selectedConditionData) {
    categoryFilters = `${categoryFilters}&${selectedConditionData.code}=${selectedConditionData.value}`
  }

  if (unref(route).params.categoryFilters) {
    const params = {
      ...unref(route).params,
      categoryFilters
    }
    return localePath({ params })
  }
  return `${unref(route).path}/${categoryFilters}`
}

const updateScrollBar = () =>  {
  scrollBarRef.value?.ps?.update()
}

const toggleShowMore = () => {
  if (scrollBarRef.value?.$el) {
    scrollBarRef.value.$el.scrollTop = 0
  }
  emit('show-more', !props.showMore)
}

const onChange = ({ value, label }: CatalogFilterItem) => {
  emit('on-change', {
    filter_code: props.filter.code,
    filter_name: props.filter.name,
    value,
    label
  })
}

const onShow = () => {
  emit('on-show', {
    filter_code: props.filter.code,
    filter_name: props.filter.name,
  })
}

watch(() => items.value.length, (oldValue, newValue) => {
  if (oldValue !== newValue) {
    nextTick(updateScrollBar)
  }
})
</script>

<style lang="scss" scoped>
.checkbox-filter {
  &__input-wrapper {
    position: relative;
  }
  &__input {
    margin-bottom: 1.6rem;
  }

  &__link {
    text-decoration: none;
    color: inherit;
  }
  &__item {
    display: flex;
    font-size: 1.4rem;
    line-height: 2.1rem;
    color: $font-color;

    span {
      color: $light-gray-color;
    }

    &.disabled-color {
      color: $gray-color;
    }
  }

  &__button {
    margin-top: 1.6rem;
    font-size: 1.2rem;
    display: inline-block;
    @media only screen and (min-width: 1024px) {
      margin-top: 0.8rem;
    }
  }

  &__values {
    max-height: 32.8rem;
    width: 100%;
    padding-right: 0.5rem;
    display: grid;
    grid-gap: 0.4rem;

    @media only screen and (max-width: 1024px) {
      grid-gap: 1.1rem;
      max-height: 240px;
    }
  }
}

/*animation*/
.filter-input {
  &-enter-active,
  &-leave-active {
    transition: opacity 500ms 100ms, max-height $transition-time,
      margin-bottom $transition-time;
  }

  &-leave-active {
    transition: $transition-time, max-height 350ms 50ms,
      margin-bottom 350ms 50ms;
  }

  &-enter {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
    overflow: hidden;
  }

  &-enter-to {
    opacity: 1;
    max-height: 4.2rem;
    margin-bottom: 1.6rem;
  }

  &-leave {
    opacity: 1;
    max-height: 4.2rem;
  }

  &-leave-to {
    opacity: 0;
    max-height: 0;
    margin-bottom: 0;
  }
}

.filter-value {
  &-enter-active,
  &-move {
    transition: $transition-time cubic-bezier(0.59, 0.12, 0.34, 0.95);
    transition-property: opacity, transform;
  }

  &-leave-active {
    transition: 70ms cubic-bezier(0.59, 0.12, 0.34, 0.95);
    transition-property: opacity, transform;
  }

  &-enter {
    opacity: 0;
    transform: translateY(31px) scaleY(0.5);
  }

  &-leave-to {
    opacity: 0;
    transform: translateY(0) scaleY(0);
    transform-origin: center top;
  }
}
/*  */
</style>
